import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getMemberByEmail,
  getMemberInterests,
  getMemberVolunteerHours,
  getMemberAttendance,
  getMemberPayments,
} from '../../../lib/supabase';
import { getPickListValues, PICK_LIST_CATEGORIES } from '../../../lib/pickLists';
import type { Tables } from '../../../types/database.types';

// ═══════════════════════════════════════════════════════════════════════════
//  ACCOUNT-PAGE READS — follows the QUERY-HOOK PATTERN documented in
//  src/pages/Admin/members/hooks/useMembers.ts. Mirror that reference; do NOT
//  invent a different shape.
// ═══════════════════════════════════════════════════════════════════════════
//
//  The account page fans out from the signed-in user's email into five reads:
//  the member row, then interests / volunteer hours / attendance / payments
//  keyed on that member's id, plus the T-shirt-size pick list. The member read
//  gates the rest via React Query's `enabled` flag (dependent queries).
//
//  Following the reference's key/derive split: SERVER inputs (the email, the
//  resolved memberId) live in the query keys so each distinct response gets its
//  own cache entry. CLIENT-side derivation — the membership-status badge that
//  AccountPage computes from the fetched payments — is done in a `useMemo` over
//  the query data in the component, NOT keyed on, exactly as the members
//  reference keeps `membershipAsOfDate` out of its key.

// ────────────────────────────────────────────────────────────
// Query keys — the single source of truth for this domain's cache entries.
// ────────────────────────────────────────────────────────────
export const queryKeys = {
  /** Broadest key — invalidate this to refetch every account-domain query. */
  all: ['account'] as const,
  /** The member row resolved from the signed-in user's email. */
  member: (email: string) => [...queryKeys.all, 'member', email] as const,
  /** A member's selected interests. */
  interests: (memberId: string) =>
    [...queryKeys.all, memberId, 'interests'] as const,
  /** A member's logged volunteer hours. */
  volunteerHours: (memberId: string) =>
    [...queryKeys.all, memberId, 'volunteer-hours'] as const,
  /** A member's meeting attendance. */
  attendance: (memberId: string) =>
    [...queryKeys.all, memberId, 'attendance'] as const,
  /** A member's payment history. */
  payments: (memberId: string) =>
    [...queryKeys.all, memberId, 'payments'] as const,
  /** T-shirt-size pick list (edit-form options). */
  shirtSizes: () => [...queryKeys.all, 'shirt-sizes'] as const,
};

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export type PaymentRow = Tables<'payments'>;
export type VolunteerHoursRow = Tables<'volunteer_hours'>;
export type MemberRow = Tables<'members'>;

export interface AccountInterest {
  id: string;
  name: string;
  category: { id: string; name: string } | null;
}

export interface AccountAttendance {
  id: string;
  date: string;
  meeting_type: string;
}

export interface ShirtSizeOption {
  value: string;
  label: string;
}

// ────────────────────────────────────────────────────────────
// Fetch functions (queryFns) — one Supabase read each; throw on error.
// Thin wrappers over the existing lib/supabase helpers so the query logic is
// reused verbatim rather than rewritten.
// ────────────────────────────────────────────────────────────

async function fetchMember(email: string): Promise<MemberRow> {
  const { member, error } = await getMemberByEmail(email);
  if (error) throw error;
  // A missing member (no record for this email) is a valid "not found" result,
  // not an error — surface it as null-throwing so the component's "No
  // Membership Found" path still renders. React Query treats a thrown error as
  // the error state; here we return the member (possibly null) instead.
  if (!member) throw new Error('MEMBER_NOT_FOUND');
  return member;
}

async function fetchInterests(memberId: string): Promise<AccountInterest[]> {
  const { interests, error } = await getMemberInterests(memberId);
  if (error) throw error;
  return interests ?? [];
}

async function fetchVolunteerHours(
  memberId: string
): Promise<VolunteerHoursRow[]> {
  const { hours, error } = await getMemberVolunteerHours(memberId);
  if (error) throw error;
  return hours ?? [];
}

async function fetchAttendance(memberId: string): Promise<AccountAttendance[]> {
  const { attendance, error } = await getMemberAttendance(memberId);
  if (error) throw error;
  return (attendance ?? [])
    .filter(
      (a): a is typeof a & { meetings: NonNullable<typeof a.meetings> } =>
        a.meetings !== null
    )
    .map((a) => ({
      id: a.id,
      date: a.meetings.date,
      meeting_type: a.meetings.title,
    }));
}

async function fetchPayments(memberId: string): Promise<PaymentRow[]> {
  const { payments, error } = await getMemberPayments(memberId);
  if (error) throw error;
  return payments ?? [];
}

async function fetchShirtSizes(): Promise<ShirtSizeOption[]> {
  const values = await getPickListValues(PICK_LIST_CATEGORIES.TSHIRT_SIZES);
  return values.map((value) => ({ value: value.value, label: value.name }));
}

// ────────────────────────────────────────────────────────────
// Hook return shape
// ────────────────────────────────────────────────────────────

export interface UseAccountDataReturn {
  member: MemberRow | null;
  interests: AccountInterest[];
  volunteerHours: VolunteerHoursRow[];
  attendance: AccountAttendance[];
  payments: PaymentRow[];
  shirtSizes: ShirtSizeOption[];
  /** True until the member row and its dependent reads have loaded once. */
  isLoading: boolean;
  /** A dependent read (interests/hours/attendance/payments) failed. */
  isError: boolean;
  /** The member row could not be loaded — genuine error OR no record found.
   *  Mirrors the old `if (error || !member)` branch. */
  memberNotFound: boolean;
}

// ────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────

/**
 * Loads everything AccountPage needs for the signed-in user, via React Query.
 * Pass the authenticated email; reads are gated until it is available.
 */
export function useAccountData(email: string | undefined): UseAccountDataReturn {
  const memberQuery = useQuery({
    queryKey: queryKeys.member(email ?? ''),
    queryFn: () => fetchMember(email ?? ''),
    enabled: Boolean(email),
  });

  const member = memberQuery.data ?? null;
  const memberId = member?.id ?? '';
  const dependentsEnabled = Boolean(memberId);

  const interestsQuery = useQuery({
    queryKey: queryKeys.interests(memberId),
    queryFn: () => fetchInterests(memberId),
    enabled: dependentsEnabled,
  });

  const volunteerHoursQuery = useQuery({
    queryKey: queryKeys.volunteerHours(memberId),
    queryFn: () => fetchVolunteerHours(memberId),
    enabled: dependentsEnabled,
  });

  const attendanceQuery = useQuery({
    queryKey: queryKeys.attendance(memberId),
    queryFn: () => fetchAttendance(memberId),
    enabled: dependentsEnabled,
  });

  const paymentsQuery = useQuery({
    queryKey: queryKeys.payments(memberId),
    queryFn: () => fetchPayments(memberId),
    enabled: dependentsEnabled,
  });

  // The pick list is independent of the member — it can load in parallel.
  const shirtSizesQuery = useQuery({
    queryKey: queryKeys.shirtSizes(),
    queryFn: fetchShirtSizes,
    enabled: Boolean(email),
  });

  // The member read failing — for ANY reason (a genuine error, or the
  // MEMBER_NOT_FOUND sentinel) — is the case the component renders as its "No
  // Membership Found" screen, exactly as the old `if (error || !member)` branch
  // did. `isError` is reserved for a dependent read (interests / hours /
  // attendance / payments) throwing, which mapped to the old catch-all.
  const memberNotFound = memberQuery.isError;

  const isError =
    interestsQuery.isError ||
    volunteerHoursQuery.isError ||
    attendanceQuery.isError ||
    paymentsQuery.isError;

  // Mirror the old control flow: loading is true until the member resolves and,
  // when it does, until its dependent reads settle. If there's no email yet
  // (auth still resolving upstream) the page also shows its loading state.
  // Once the member read fails (error or not-found) we stop loading and the
  // component renders its "No Membership Found" screen.
  const isLoading = useMemo(() => {
    if (!email) return true;
    if (memberQuery.isPending) return true;
    if (memberQuery.isError) return false;
    return (
      interestsQuery.isPending ||
      volunteerHoursQuery.isPending ||
      attendanceQuery.isPending ||
      paymentsQuery.isPending
    );
  }, [
    email,
    memberQuery.isPending,
    memberQuery.isError,
    interestsQuery.isPending,
    volunteerHoursQuery.isPending,
    attendanceQuery.isPending,
    paymentsQuery.isPending,
  ]);

  return {
    member,
    interests: interestsQuery.data ?? [],
    volunteerHours: volunteerHoursQuery.data ?? [],
    attendance: attendanceQuery.data ?? [],
    payments: paymentsQuery.data ?? [],
    shirtSizes: shirtSizesQuery.data ?? [],
    isLoading,
    isError,
    memberNotFound,
  };
}
